import { useState, useEffect, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../store';
import { AuthorizedUser } from '@shared/sdk-v1/models/AuthorizedUser';
import {
  addAuthorizedUser,
  deleteAuthorizedUser,
  fetchAuthorizedUsers,
  getAuthorizedUserError,
  getAuthorizedUsers,
  getIsAuthorizedUserLoading,
  updateAuthorizedUser,
} from '@state/authorizedUsersSlice';
import { getUser } from '@state/authSlice';
import { showErrorToast } from '../utils/toast';
import { setIsLoading } from '@state/courierSlice';
import { useTrackEvent } from '../utils/track';
import { toE164 } from '../utils/phone';

interface UserForm {
  id?: number;
  name: string;
  phone: string;
  idNumber: string;
  idType: string;
  createdAt?: Date;
  updatedAt?: Date;
  courierUserId?: number;
}

export const useAuthorizedUsers = () => {
  const dispatch = useDispatch<AppDispatch>();
  const track = useTrackEvent();
  const user = useSelector(getUser);
  const authorizedUsers = useSelector(getAuthorizedUsers);
  const isLoading = useSelector(getIsAuthorizedUserLoading);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const error = useSelector(getAuthorizedUserError);

  const [showUserForm, setShowUserForm] = useState(false);
  const [formData, setFormData] = useState<UserForm>({
    name: '',
    phone: '',
    idNumber: '',
    idType: '',
    courierUserId: user?.id,
  });

  // Fetch users when user id changes
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchAuthorizedUsers(user.id));
    }
  }, [dispatch, user?.id]);

  // Show empty form for adding new user
  const showAddUserForm = () => {
    setFormData({
      name: '',
      phone: '',
      idNumber: '',
      idType: '',
      courierUserId: user?.id,
    });
    setShowUserForm(true);
  };

  // Show form with existing user data for editing
  const showEditUserForm = (user: AuthorizedUser) => {
    setFormData({
      id: user.id,
      name: user.name || '',
      phone: toE164(user.phone || ''),
      idNumber: user.idNumber?.toString() || '',
      idType: user.idType || '',
      courierUserId: user.courierUserId,
    });
    setShowUserForm(true);
  };

  // Delete user handler
  const deleteUser = async (user: AuthorizedUser) => {
    if (!user.id) {
      showErrorToast({
        title: 'Error',
        message: 'User ID missing – cannot delete!',
      });
      return;
    }
    try {
      setIsLoading(true);
      await dispatch(deleteAuthorizedUser(user.id)).unwrap();
    } catch (err) {
      console.error('Failed to delete user:', err);
      showErrorToast({
        title: 'Error',
        message: 'Failed to delete authorized user.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Save new or updated user
  const saveUser = async () => {
    try {
      if (!formData.name || !formData.phone) {
        showErrorToast({
          title: 'Error',
          message: 'Please fill in all required fields.',
        });
        return;
      }

      setIsSubmitting(true);

      if (formData.id) {
        await dispatch(
          updateAuthorizedUser({
            id: formData.id,
            name: formData.name,
            phone: formData.phone,
            idNumber: formData.idNumber,
            idType: formData.idType,
            courierUserId: user?.id,
          })
        ).unwrap();
      } else {
        await dispatch(
          addAuthorizedUser({
            name: formData.name,
            phone: formData.phone,
            idNumber: formData.idNumber,
            idType: formData.idType,
            courierUserId: user?.id,
          })
        ).unwrap();
        track('authorized_user_created');
      }
      setShowUserForm(false);
    } catch (err) {
      console.error('Failed to save user: ', err);
      showErrorToast({
        title: 'Error',
        message: 'Failed to save authorized user.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel and reset form
  const cancel = () => {
    setShowUserForm(false);
    setFormData({
      name: '',
      phone: '',
      idNumber: '',
      idType: '',
    });
  };

  // Handle input changes
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return {
    showUserForm,
    formData,
    authorizedUsers,
    isLoading,
    error,
    showAddUserForm,
    showEditUserForm,
    deleteUser,
    saveUser,
    cancel,
    handleChange,
    isSubmitting,
  };
};
